import React, { useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  Tag,
  BtnPrimary,
  BtnSecondary,
} from "@/components/cathedral/Primitives";

function CopyableBlock({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      // Clipboard API blocked (insecure context, perm denied) — UX falls back to manual select
      console.warn("clipboard copy failed", e);
    }
  };
  return (
    <div className="mt-4" data-testid={`bridge-block-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <Overline tone="muted">{label}</Overline>
        <button
          onClick={copy}
          data-testid={`bridge-copy-${testid}`}
          className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] hover:text-glow-cyan transition-colors px-2 py-1 border border-[var(--chrome)] hover:border-[var(--cyan)]"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre className="yaml-block rounded-none whitespace-pre-wrap break-all text-[11px] leading-relaxed max-h-72 overflow-auto">
        {value}
      </pre>
    </div>
  );
}

function NodePack({ pack }) {
  const [tab, setTab] = useState("env");
  const tabs = [
    { id: "env",    label: ".env Block",      key: "env_block" },
    { id: "curl1",  label: "curl · execute",  key: "curl_execute" },
    { id: "curl2",  label: "curl · ledger",   key: "curl_ledger" },
    { id: "py",     label: "Python Adapter",  key: "python_snippet" },
    { id: "js",     label: "Node.js Adapter", key: "node_snippet" },
    { id: "cors",   label: "CORS Hint",       key: "cors_hint" },
  ];
  const active = tabs.find((t) => t.id === tab) || tabs[0];

  return (
    <Card className="p-7" data-testid={`bridge-pack-${pack.node_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Overline tone="cyan">Federation Node · {pack.role}</Overline>
          <h4 className="font-display text-2xl font-light tracking-[0.04em] mt-2 leading-none text-glow-cyan">
            {pack.node_name}
          </h4>
          <div className="font-mono text-[10px] text-[var(--text-3)] tracking-[0.2em] uppercase mt-3 flex gap-3 flex-wrap">
            <span>id · {pack.node_id}</span>
            <span>· {pack.node_url}</span>
            {pack.vertical && <Tag tone="cite">{pack.vertical}</Tag>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`bridge-tab-${pack.node_id}-${t.id}`}
            className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-all ${
              tab === t.id
                ? "border-[var(--cyan)] text-glow-cyan bg-[rgba(0,240,255,0.04)]"
                : "border-[var(--chrome)] text-[var(--text-3)] hover:text-[var(--text)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <CopyableBlock
        label={active.label}
        value={pack[active.key]}
        testid={`${pack.node_id}-${active.id}`}
      />
    </Card>
  );
}

export default function FederationBridgePack() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const fetchPacks = async () => {
    setBusy(true);
    setErr(null);
    try {
      setData(await api.bridgePackAll());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card glow="gold" className="p-10" data-testid="federation-bridge-pack">
      <Overline tone="gold">Federation Bridge Pack · 5 External Monoliths</Overline>
      <h3 className="font-display text-3xl font-light tracking-[0.04em] mt-3 mb-4 text-glow-gold leading-none">
        WIRE THE FEDERATION
      </h3>
      <p className="text-sm text-[var(--text-2)] leading-relaxed max-w-3xl">
        One-click generation of the ready-to-paste integration kit for every external monolith
        (Aria, Roger AI, Axiom). Each pack contains an{" "}
        <code className="text-glow-cyan">.env</code> block, two{" "}
        <code className="text-glow-cyan">curl</code> examples, a Python adapter, a Node.js adapter,
        and a CORS hint. Drop the snippet into each external app and the cutover is complete —
        no peer-to-peer chatter, no duplicated knowledge base, one shared brain.
      </p>

      <div className="mt-6 flex gap-3 flex-wrap">
        <BtnPrimary onClick={fetchPacks} disabled={busy} data-testid="bridge-pack-fetch">
          {busy ? "Generating…" : data ? "Regenerate Packs" : "Generate Bridge Packs"}
        </BtnPrimary>
        {data && (
          <BtnSecondary
            onClick={() => setData(null)}
            data-testid="bridge-pack-clear"
          >
            Hide
          </BtnSecondary>
        )}
      </div>

      {err && (
        <div className="mt-5 font-mono text-xs text-glow-red" data-testid="bridge-pack-err">
          {err}
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-6">
          <div className="font-mono text-[11px] text-[var(--text-3)] tracking-[0.22em] uppercase">
            CENTRAL BRAIN URL ·{" "}
            <span className="text-glow-cyan">{data.central_brain_url || "(unset)"}</span>
            <span className="mx-3">·</span>
            <span className="text-glow-gold">{data.node_count} bridges</span>
          </div>
          {data.packs.map((p) => (
            <NodePack key={p.node_id} pack={p} />
          ))}
        </div>
      )}
    </Card>
  );
}
